import { XCircleIcon } from "@heroicons/react/24/solid";
import CharacterPortrait from "../CharacterPortrait/CharacterPortrait";
import Button from "../Button/Button";
import "./styles/characterCard.scss";
import { useNavigate } from "react-router";
import Modal from "../Modal/Modal";
import VersionPicker from "../VersionPicker/VersionPicker";
import { useState } from "react";
import { transformToCharacterListVersion } from "@src/utils/character-utils";
import { useCharacterStore } from "@src/stores/characterStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@src/utils/axios-instance";
import toast from "react-hot-toast";
import { CharacterListVersion, CharacterWithVersion, Version } from "@src/types/global";
interface IProps {
    character: CharacterListVersion;
    onClick: (id: string) => void;
    type?: "horizontal" | "vertical";
}

/**
 * Shows a character in card version, allowing the user to add them to their stack, delete them or view their page
 * The component comes in two layout, each exposing their own logic :
 * - Horizontal : What you would find in the charater stack. It enables the version switch and only allows to delete the character from the stack
 * - Vertical : What you find on the characters index page. It enables the "add to stack" and delete character actions.
 * @component
 * @param {character} character - The character whose information populate the card
 * @param {fn} onClick -  The function called on layout = horizontal when clicking on the cross icon
 * @param {"horizontal" | "vertical"} type - Default: "horizontal"  the layout to use
 *
 * */
const CharacterCard = ({ character, onClick, type = "horizontal", ...rest }: IProps) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const { addVersionToList, characterList, swapCharacterVersion } =
        useCharacterStore((state) => state);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    async function deleteCharacter(id: string) {
        const res = await api.delete(`/characters/${id}`);
        return res.data;
    }
    const mutation = useMutation({
        mutationKey: ["characters"],
        mutationFn: deleteCharacter,
        onSuccess: (_, id) => {
            toast.success("Character deleted.");
            queryClient.setQueryData(
                ["characters"],
                (prevData: { characters: CharacterListVersion[] }) => {
                    const newChars = prevData.characters.filter(
                        (char: CharacterListVersion) => char.id != id,
                    );
                    return { ...prevData, characters: newChars };
                },
            );
        },
    });

    return (
        <>
            <div className={`character-card character-card--${type}`} {...rest}>
                {type == "vertical" && (
                    <>
                        <span className="character-card--vertical__ellipsis">
                            <strong>. . .</strong>
                            <div className="character-card--vertical__ellipsis__actions">
                                <Button
                                    type="button"
                                    fn={() => {
                                        mutation.mutate(character.id);
                                    }}
                                    preset="stealth"
                                >
                                    Delete
                                </Button>
                            </div>
                        </span>
                    </>
                )}
                <div
                    className={`character-card--${type}__character`}
                    onClick={() => {
                        navigate(`/characters/${character.id}`);
                    }}
                >
                    <CharacterPortrait
                        avatar={character.avatar}
                        size={type == "horizontal" ? "small" : "medium"}
                    />
                    <div className={`character-card--${type}__info`}>
                        {type == "horizontal" && (
                            <button
                                className={`character-card--${type}__info__version`}
                                name="change_version"
                                aria-label="Change the character version (if any are available)"
                                title="Change version"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setModalIsOpen(true);
                                }}
                            >
                                {character.version_name}
                            </button>
                        )}
                        <p className={`character-card--${type}__info__name`}>
                            {character.first_name}
                        </p>
                    </div>
                </div>
                {type == "horizontal" ? (
                    <button
                        className={`character-card--${type}__delete`}
                        title="Remove from stack"
                        aria-label={`Remove ${character.first_name} from the roster`}
                        onClick={() => onClick(character.id)}
                    >
                        <XCircleIcon />
                    </button>
                ) : (
                    <Button
                        type="button"
                        preset="stealth"
                        aria-label={`Add ${character.first_name} from the roster`}
                        fn={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            addVersionToList(
                                character as any as CharacterWithVersion,
                            )
                        }
                        }
                    >
                        Add to stack
                    </Button>
                )}
            </div>

            <Modal
                isOpen={modalIsOpen}
                closeModalFn={() => setModalIsOpen(false)}
            >
                <VersionPicker
                    character={character}
                    onClick={(
                        version: Version,
                        originalCharacterObject: CharacterListVersion,
                    ) => {
                        const character = structuredClone(
                            originalCharacterObject,
                        );
                        //@ts-expect-error adding the character_versions key on purpose so that the item gets transformed
                        character.character_versions = version;
                        const characterListVersion =
                            transformToCharacterListVersion(
                                character as any as CharacterWithVersion,
                            );
                        if (characterList !== null) {
                            swapCharacterVersion(
                                character,
                                characterListVersion,
                            );
                        } else {
                            addVersionToList(
                                character as any as CharacterWithVersion,
                            );
                        }
                        setModalIsOpen(false);
                    }}
                />
            </Modal>
        </>
    );
};
export default CharacterCard;
